import { motion, Reorder, useDragControls } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  GripVertical, 
  Edit3, 
  Trash2, 
  ChevronDown,
  CheckCircle
} from "lucide-react";

export default function DraggableClauseList({ 
  clauses, 
  onReorder, 
  onEdit, 
  onRemove,
  language,
  getLocalizedText 
}) {
  return (
    <Reorder.Group 
      axis="y" 
      values={clauses} 
      onReorder={onReorder}
      className="space-y-2"
    >
      {clauses.map((clause, index) => (
        <DraggableClauseItem
          key={clause.id}
          clause={clause}
          index={index}
          onEdit={onEdit}
          onRemove={onRemove}
          language={language}
          getLocalizedText={getLocalizedText}
        />
      ))}
    </Reorder.Group>
  );
}

function DraggableClauseItem({ clause, index, onEdit, onRemove, language, getLocalizedText }) {
  const dragControls = useDragControls();
  
  return (
    <Reorder.Item
      value={clause}
      dragListener={false}
      dragControls={dragControls}
      className="bg-white"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, x: -100 }}
        transition={{ duration: 0.2 }}
        className="border-2 border-slate-200 rounded-lg p-3 hover:border-blue-300 transition group"
      >
        <div className="flex items-center gap-3">
          {/* Drag Handle */}
          <motion.div
            onPointerDown={(e) => dragControls.start(e)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded"
          >
            <GripVertical className="h-5 w-5 text-slate-400" />
          </motion.div>

          {/* Clause Number */}
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold flex-shrink-0">
            {index + 1}
          </div>

          {/* Clause Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-medium text-slate-900 truncate">
                {getLocalizedText(clause.title)}
              </p>
              {clause.is_required && (
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-300">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {language === "fr" ? "Obligatoire" : "Required"}
                </Badge>
              )}
              {clause.is_locally_modified && (
                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                  {language === "fr" ? "Modifiée" : "Modified"}
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-600 line-clamp-1">
              {clause.category}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 transition">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(clause)}
              className="h-8 w-8 p-0"
            >
              <Edit3 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemove(clause.id)}
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </motion.div>
    </Reorder.Item>
  );
}
